import { useCallback, useEffect, useRef, useState } from 'react'

import { env } from '@/env'
import { useDigitalProductContext } from '@/shared/contexts/DigitalProductContext'
import { useUserProgress } from '@/shared/hooks/useUserProgress'
import { useRewardGainActivityStore } from '@/shared/store/rewardGainActivityStore'

import {
  buildRewardGainBatch,
  REWARD_GAIN_BADGE_DURATION_MS,
  REWARD_GAIN_BADGE_STAGGER_MS
} from '../lib/rewardGainQueue'
import { ackRewardGain } from '../model/mutations'

import type { RewardGainBatch } from '../model/types'

const isDev = env.NODE_ENV === 'development'

type QueueSlot =
  | { kind: 'item'; index: number }
  | { kind: 'overflow' }

export type UseRewardGainQueueResult = {
  batch: RewardGainBatch | null
  slot: QueueSlot | null
}

/**
 * Orquestra a fila de badges de ganho.
 *
 * Um lote é congelado ao sair de `idle` e não muda enquanto está sendo exibido —
 * movimentos que chegam por refetch no meio da fila formam o lote seguinte, não
 * se injetam no corrente.
 */
export const useRewardGainQueue = (): UseRewardGainQueueResult => {
  const { digitalProductId } = useDigitalProductContext()
  const { userProgress, refetchProgress } = useUserProgress()

  const [batch, setBatch] = useState<RewardGainBatch | null>(null)
  const [slot, setSlot] = useState<QueueSlot | null>(null)

  // Lotes já processados nesta sessão, por ackId. Sem isso, um ACK que falha faz
  // o mesmo lote voltar a cada refetch e a fila entra em loop.
  const handledAckIds = useRef<Set<number>>(new Set())
  const isBusy = useRef(false)

  const setActive = useRewardGainActivityStore(s => s.setActive)

  const movements = userProgress?.unseen_wallet_movements
  const rewardCount = userProgress?.unseen_reward_movements_count ?? 0

  const acknowledge = useCallback(
    async (ackId: number) => {
      try {
        await ackRewardGain({ digitalProductId, lastMovementId: ackId })
        refetchProgress()
      } catch (error) {
        // ACK é silencioso por natureza: o participante não pediu nada e não deve
        // ver toast de falha. Sem retry — repetir o badge num ciclo posterior é
        // aceitável, pular um ganho não é.
        if (isDev) console.warn('[rewardGain] ACK falhou', error)
      }
    },
    [digitalProductId, refetchProgress]
  )

  useEffect(() => {
    if (isBusy.current) return undefined
    if (!movements || movements.length === 0) return undefined

    const next = buildRewardGainBatch(movements, rewardCount)
    if (!next || handledAckIds.current.has(next.ackId)) return undefined

    handledAckIds.current.add(next.ackId)
    isBusy.current = true
    setBatch(next)
    // Publica "ocupado" para quem espera a fila esvaziar (nudge da lojinha).
    setActive(true)

    // Lote sem nada exibível ainda precisa avançar o cursor.
    if (next.items.length === 0 && next.overflowCount === 0) {
      void acknowledge(next.ackId).finally(() => {
        isBusy.current = false
        setBatch(null)
        setActive(false)
      })
      return undefined
    }

    const slots: QueueSlot[] = next.items.map((_, index) => ({
      kind: 'item' as const,
      index
    }))
    if (next.overflowCount > 0) slots.push({ kind: 'overflow' })

    const timers: ReturnType<typeof setTimeout>[] = []
    const step = REWARD_GAIN_BADGE_DURATION_MS + REWARD_GAIN_BADGE_STAGGER_MS

    slots.forEach((current, index) => {
      timers.push(setTimeout(() => setSlot(current), index * step))
      timers.push(
        setTimeout(() => setSlot(null), index * step + REWARD_GAIN_BADGE_DURATION_MS)
      )
    })

    timers.push(
      setTimeout(() => {
        void acknowledge(next.ackId).finally(() => {
          isBusy.current = false
          setBatch(null)
          setActive(false)
        })
      }, slots.length * step)
    )

    return () => timers.forEach(clearTimeout)
  }, [movements, rewardCount, acknowledge, setActive])

  return { batch, slot }
}
